import { createSelector } from "@reduxjs/toolkit";
import { RootState } from "@/app/appStore";
import {
  GondolaChangeGroupsData,
  SKUSectionChangeGroup,
  ChangeGroupsFilterParams,
} from "@/entities/gondola-change-groups/model/types";
import { GondolaChangeGroup } from "@/entities/gondola-change-groups/model/types";
import { applyRangeFilter } from "@/features/gondola-change-groups/lib/helpers";
import {
  FILTER_OPTIONS,
  FilterOption,
} from "@/features/gondola-change-groups/lib/const";

const selectGondolaChangeGroupsState = (state: RootState) =>
  state.gondolaChangeGroups;

export const selectGondolaChangeGroupsFilters = createSelector(
  selectGondolaChangeGroupsState,
  (gondolaChangeGroups) => gondolaChangeGroups.filters
);

const matchesAcceptedSkuSectionGroups = (
  sscg: SKUSectionChangeGroup,
  option: FilterOption
): boolean => {
  switch (option) {
    case FILTER_OPTIONS.ZERO:
      return sscg.is_accepted === false && sscg.detections_count > 0;
    case FILTER_OPTIONS.ANY:
      return sscg.detections_count > 0;
    case FILTER_OPTIONS.RANGE:
      return sscg.is_accepted;
    // All
    default:
      return true;
  }
};

const filterSkuSectionChangeGroup = (
  sscg: SKUSectionChangeGroup,
  filters: ChangeGroupsFilterParams
): boolean => {
  // Filter by selectedSkuBaseId
  if (filters.selectedSkuBaseId !== undefined) {
    const sscgSkuId = String(sscg.sku_base_id);
    const filterSkuId = String(filters.selectedSkuBaseId);
    if (sscgSkuId !== filterSkuId) return false;
  }

  // Filter by acceptedSkuSectionGroups
  if (
    filters.acceptedSkuSectionGroups &&
    filters.acceptedSkuSectionGroups.option !== FILTER_OPTIONS.ALL
  ) {
    const { option } = filters.acceptedSkuSectionGroups;
    if (!matchesAcceptedSkuSectionGroups(sscg, option)) {
      return false;
    }
  }

  // Filter by intersectedAnnotations using has_annotation
  if (
    filters.intersectedAnnotations &&
    filters.intersectedAnnotations.option !== FILTER_OPTIONS.ALL
  ) {
    const hasAnnotationCount = sscg.has_annotation ? 1 : 0;
    if (!applyRangeFilter(filters.intersectedAnnotations, hasAnnotationCount)) {
      return false;
    }
  }

  // Filter by intersectedProxs using intersected_proxs_count
  if (
    filters.intersectedProxs &&
    filters.intersectedProxs.option !== FILTER_OPTIONS.ALL
  ) {
    if (
      !applyRangeFilter(filters.intersectedProxs, sscg.intersected_proxs_count)
    ) {
      return false;
    }
  }

  return true;
};

// Helper function to filter SKU section change groups within a collection
const filterSkuSectionChangeGroups = (
  skuSectionChangeGroups: SKUSectionChangeGroup[],
  filters: ChangeGroupsFilterParams
): SKUSectionChangeGroup[] => {
  return skuSectionChangeGroups.filter((sscg) =>
    filterSkuSectionChangeGroup(sscg, filters)
  );
};

// Helper function to check if a gondola change group has any matching SKU sections
const hasMatchingSkuSections = (
  group: GondolaChangeGroup,
  filters: ChangeGroupsFilterParams
): boolean => {
  const filteredSkuSectionChangeGroups = filterSkuSectionChangeGroups(
    group.sku_section_change_groups,
    filters
  );
  return filteredSkuSectionChangeGroups.length > 0;
};

export const selectFilteredChangeGroups = createSelector(
  [
    (
      _state: RootState,
      gondolaChangeGroupsData: GondolaChangeGroupsData | null
    ) => gondolaChangeGroupsData?.gondola_change_groups || [],
    selectGondolaChangeGroupsFilters,
  ],
  (gondolaChangeGroups, filters) => {
    if (!gondolaChangeGroups.length) return [];

    return gondolaChangeGroups.filter((group: GondolaChangeGroup) =>
      hasMatchingSkuSections(group, filters)
    );
  }
);

export const selectSelectedGondolaChangeGroup = createSelector(
  [
    selectGondolaChangeGroupsState,
    (
      _state: RootState,
      gondolaChangeGroupsData: GondolaChangeGroupsData | null
    ) => gondolaChangeGroupsData?.gondola_change_groups || [],
  ],
  (gondolaChangeGroupsState, gondolaChangeGroups) => {
    const selectedId = gondolaChangeGroupsState.selectedGondolaChangeGroupId;
    if (!selectedId || !gondolaChangeGroups.length) return null;
    return (
      gondolaChangeGroups.find(
        (group) => group.gondola_change_group_composite_id === selectedId
      ) || null
    );
  }
);

export const selectFilteredSkuSectionChangeGroups = createSelector(
  [selectSelectedGondolaChangeGroup, selectGondolaChangeGroupsFilters],
  (selectedGondolaChangeGroup, filters) => {
    if (!selectedGondolaChangeGroup) return [];

    const skuSectionChangeGroups =
      selectedGondolaChangeGroup.sku_section_change_groups || [];

    return filterSkuSectionChangeGroups(skuSectionChangeGroups, filters);
  }
);

export const selectFilteredGondolaChanges = createSelector(
  [selectSelectedGondolaChangeGroup, selectFilteredSkuSectionChangeGroups],
  (selectedGondolaChangeGroup, filteredSkuSectionChangeGroups) => {
    if (!selectedGondolaChangeGroup || !filteredSkuSectionChangeGroups.length) {
      return [];
    }

    const filteredSkuSectionChangeIds = new Set<string>();

    filteredSkuSectionChangeGroups.forEach((sscg: SKUSectionChangeGroup) => {
      sscg.sku_section_changes.forEach((skuSectionChange) => {
        filteredSkuSectionChangeIds.add(
          skuSectionChange.gondola_change_camera_composite_id
        );
      });
    });

    return selectedGondolaChangeGroup.gondola_changes.filter(
      (gondolaChange) => {
        return filteredSkuSectionChangeIds.has(
          gondolaChange.gondola_change_camera_composite_id
        );
      }
    );
  }
);

export const selectSelectedGondolaChange = createSelector(
  [selectGondolaChangeGroupsState, selectFilteredGondolaChanges],
  (gondolaChangeGroupsState, filteredGondolaChanges) => {
    const selectedGondolaChangeId =
      gondolaChangeGroupsState.selectedGondolaChangeId;
    if (!selectedGondolaChangeId || !filteredGondolaChanges.length) return null;
    return (
      filteredGondolaChanges.find(
        (change) =>
          change.gondola_change_camera_composite_id === selectedGondolaChangeId
      ) || null
    );
  }
);

export const selectTrackAvatarBindingFinder = createSelector(
  [
    (state: RootState) => state.gondolaChangeGroups.trackAvatarBindings,
    (state: RootState) => state.gondolaChangeGroups.recoveredTrackIdMapping,
  ],
  (trackAvatarBindings, recoveredTrackIdMapping) => {
    return (trackId: number) => {
      if (trackAvatarBindings[trackId]) {
        return trackAvatarBindings[trackId];
      }

      const originalTrackId = recoveredTrackIdMapping[trackId];
      if (originalTrackId && trackAvatarBindings[originalTrackId]) {
        return trackAvatarBindings[originalTrackId];
      }

      // No binding found
      return null;
    };
  }
);
